import type { CronListParams, CronStatus } from '../types'

export { formatChangeLogCreatedAt as formatCronChangeLogCreatedAt } from '@/lib/utils/formatChangeLogCreatedAt'

/** Strip tags / entities so table cells never render raw HTML (avoids layout breaks). */
export function cronDescriptionPlainText(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** True when CKEditor / HTML body has no visible text (Angular required parity). */
export function isCronDescriptionEffectivelyEmpty(html: string): boolean {
  return cronDescriptionPlainText(html).length === 0
}

/** Angular list preview: first 100 chars of description + ellipsis. */
export function cronDescriptionPreview(html: string, maxLen = 180): string {
  const plain = cronDescriptionPlainText(html)
  if (!plain) return ''
  return plain.length > maxLen ? `${plain.slice(0, maxLen)}...` : plain
}

export function normalizeCronListParams(q: string, cronStatus: CronStatus | ''): CronListParams {
  const out: CronListParams = {}
  const trimmed = q.trim()
  if (trimmed) out.q = trimmed
  if (cronStatus) out.cron_status = cronStatus
  return out
}

export function cronListParamsEqual(a: CronListParams, b: CronListParams): boolean {
  const aq = a.q ?? ''
  const bq = b.q ?? ''
  const as = a.cron_status ?? ''
  const bs = b.cron_status ?? ''
  return aq === bq && as === bs
}

const WEEKDAY_LABELS: Record<string, string> = {
  '0': 'Sunday',
  '1': 'Monday',
  '2': 'Tuesday',
  '3': 'Wednesday',
  '4': 'Thursday',
  '5': 'Friday',
  '6': 'Saturday',
  '7': 'Sunday',
}

/** Cron IDs scheduled with monthlyOn in Console Kernel (no monthly DB column). */
const MONTHLY_CRON_IDS = new Set([
  'LIBERTY_REPORT_MONTHLY',
  'CLEAR_MONTHLY_PAID_VALUE',
  'AI_CASH_FLOW',
])

function looksMonthly(job: {
  cron_id?: string | number | null
  cron_name?: string | null
}): boolean {
  const id = String(job.cron_id ?? '').trim().toUpperCase()
  if (id && MONTHLY_CRON_IDS.has(id)) return true
  if (id.includes('MONTHLY')) return true
  const name = String(job.cron_name ?? '').trim().toUpperCase()
  return name.includes('MONTHLY')
}

/** Human-readable schedule frequency from cron_time / weekly_cron / known monthly jobs. */
export function cronFrequencyKind(job: {
  cron_id?: string | number | null
  cron_name?: string | null
  cron_time?: string | null
  weekly_cron?: string | null
}): 'daily' | 'weekly' | 'monthly' | 'none' {
  const weekly = job.weekly_cron != null && String(job.weekly_cron).trim() !== ''
  if (weekly) return 'weekly'
  if (looksMonthly(job)) return 'monthly'
  if (job.cron_time != null && String(job.cron_time).trim() !== '') return 'daily'
  return 'none'
}

export function formatCronFrequency(job: {
  cron_id?: string | number | null
  cron_name?: string | null
  cron_time?: string | null
  weekly_cron?: string | null
}): string {
  const kind = cronFrequencyKind(job)
  if (kind === 'weekly') {
    const weekly = String(job.weekly_cron).trim()
    const day = WEEKDAY_LABELS[weekly]
    return day ? `Weekly (${day})` : 'Weekly'
  }
  if (kind === 'monthly') return 'Monthly'
  if (kind === 'daily') return 'Daily'
  return '—'
}
